import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Link,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import Homenavbar from "./Homenavbar";
import Homefooter from "./Homefooter";
import DemoRequestDialog from "./Components/DemoRequestDialog";

const sectionSx = { mb: 4 };
const headingSx = { fontWeight: 600, color: "#1E293B", mb: 2 };
const bodySx = { color: "#475569", mb: 2, lineHeight: 1.75 };

const AITermsOfUse = () => {
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleDemoOpen = () => setDemoDialogOpen(true);
  const handleDemoClose = () => setDemoDialogOpen(false);

  return (
    <Box sx={{ backgroundColor: "#F8FAFC", minHeight: "100vh" }}>
      <Homenavbar
        onRequestDemo={handleDemoOpen}
        activeLink={activeLink}
        setActiveLink={setActiveLink}
      />
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            p: { xs: 4, md: 8 },
            borderRadius: "20px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: "#1E293B",
              mb: 2,
              textAlign: "center",
            }}
          >
            AI Terms of Use
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: "#64748B", textAlign: "center", mb: 6 }}
          >
            Last Update: 24/09/2026
          </Typography>

          <Typography paragraph sx={{ ...bodySx, fontSize: "1.05rem", mb: 4 }}>
            These AI-Enabled Product and Service Terms (&quot;Terms&quot;) are
            applicable if you utilize any AI-enabled product or service offered
            by Jobs&apos;n&apos;Profiles / Jobs n Profiles Inc. (collectively,
            the &quot;AI Services&quot;), including the Jobs&apos;n&apos;Profiles
            AI Assistant on{" "}
            <strong>jobsnprofiles.com</strong> and related Bench Sales
            applications. By using the AI Services, you agree you have fully
            read, understood, and voluntarily accepted the Terms. If you do not
            agree to these Terms, you should stop accessing or using the AI
            Services immediately:
          </Typography>

          <Box component="section" sx={sectionSx}>
            <Typography variant="h5" sx={headingSx}>
              1. Third-Party Integrations and AI Output
            </Typography>
            <Typography sx={bodySx}>
              The AI Services could involve integrations with third parties,
              such as OpenAI LLC (&quot;OpenAI&quot;). During the course of
              using the AI Services, Jobs&apos;n&apos;Profiles may provide you
              with output which may include, without limitation, OpenAI
              language-generation models (LLM), incorporated into output, to be
              used as a tool to create answers to questions, descriptions,
              instructions, and other texts. You acknowledge that you are not
              required by law or otherwise to provide any specific input and
              that any input is provided on a voluntary basis. In addition, you
              acknowledge that any output generated by the AI Services
              (collectively, &quot;AI Output&quot;) is subject to, depends on,
              and is a function of the input. You acknowledge that any AI Output
              may not be unique.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box component="section" sx={sectionSx}>
            <Typography variant="h5" sx={headingSx}>
              2. Acceptable Use and Content Restrictions
            </Typography>
            <Typography sx={bodySx}>
              You may only enter content that complies with our{" "}
              <Link href="/terms-and-conditions" underline="hover">
                Terms and Conditions
              </Link>{" "}
              and{" "}
              <Link href="/privacy-policy" underline="hover">
                Privacy Policy
              </Link>
              . AI Services may automatically reject certain content that could
              be inappropriate in nature. Jobs&apos;n&apos;Profiles reserves the
              right to limit access to or terminate the use of the AI Services
              at our sole discretion.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box component="section" sx={sectionSx}>
            <Typography variant="h5" sx={headingSx}>
              3. Compliance with Policies
            </Typography>
            <Typography sx={bodySx}>
              You may not use AI Services to create or share AI Output in a
              manner that violates this document and/or any third-party
              policies, such as OpenAI policies. Jobs&apos;n&apos;Profiles
              reserves the right to limit access to or terminate the use of the
              AI Services at our sole discretion.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box component="section" sx={sectionSx}>
            <Typography variant="h5" sx={headingSx}>
              4. No Guarantee of Quality or Accuracy
            </Typography>
            <Typography sx={bodySx}>
              Jobs&apos;n&apos;Profiles does not guarantee the quality and
              accuracy of the AI Output. The AI Services are intended as
              guidance in accessing the services provided by
              Jobs&apos;n&apos;Profiles (including Bench Sales tools for
              candidates, recruiters, and admins). While Jobs&apos;n&apos;Profiles
              strives to promote the accuracy of the AI Output, you should
              proofread and confirm all AI Output is accurate and correct.
              Jobs&apos;n&apos;Profiles is not responsible for any errors in
              the AI Output.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box component="section" sx={sectionSx}>
            <Typography variant="h5" sx={headingSx}>
              5. Disclaimer of Warranties and Limitation of Liability
            </Typography>
            <Typography sx={bodySx}>
              The AI Services and AI Outputs are provided to you on an &quot;as
              is&quot; and &quot;as available&quot; basis. Jobs&apos;n&apos;Profiles
              disclaims all express warranties and all implied warranties,
              including merchantability and fitness for a particular purpose. In
              addition, AI Services were developed to support inputs in English,
              resulting in AI Outputs in English. You acknowledge that no
              security measures can fully prevent all potential loss, misuse or
              alteration of personal and other information provided as input,
              and therefore, Jobs&apos;n&apos;Profiles shall, to the fullest
              extent permitted by law, not be liable for any potential losses or
              damages arising as a result of your use of the AI Services.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box component="section" sx={sectionSx}>
            <Typography variant="h5" sx={headingSx}>
              6. Ownership of AI Output
            </Typography>
            <Typography sx={bodySx}>
              Jobs&apos;n&apos;Profiles does not own AI Output. The AI Services
              may produce similar content based on content or input created by
              other users and/or customers. Therefore, your rights in the AI
              Output may not be enforceable against other users and/or
              customers. Nonetheless, Jobs&apos;n&apos;Profiles may utilize the
              AI Output for product analysis for improvement.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box component="section" sx={sectionSx}>
            <Typography variant="h5" sx={headingSx}>
              7. Prohibited Input
            </Typography>
            <Typography sx={bodySx}>
              You agree not to input any confidential or private sensitive
              information into the AI Services. Examples of private sensitive
              information may include, without limitation, detailed
              identification information, bank account numbers, medical
              records, and financial records. To the extent that you use AI
              Services to process any category of confidential or private
              sensitive information (as defined under applicable laws), you
              shall bear all responsibilities and you shall only do so in
              compliance with all applicable laws, including, but not limited
              to: (i) providing all necessary notices and policies for
              transparency compliance; and (ii) obtaining all applicable rights
              and consents. In addition, you shall not use AI Services in
              violation of applicable laws or third-party rights or for
              unethical purposes. Except for the rights expressly granted under
              this document, no other rights, or permissions to access or use
              any of the AI Services are granted to you. You hereby agree to
              indemnify Jobs&apos;n&apos;Profiles for any third-party claim it
              receives in connection with your use of the AI Services in breach
              of any applicable data protection law, privacy, or confidentiality
              right of any third party.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box component="section" sx={sectionSx}>
            <Typography variant="h5" sx={headingSx}>
              8. Prohibited Activities
            </Typography>
            <Typography sx={bodySx}>
              You are prohibited from:
            </Typography>
            <List dense>
              {[
                "utilizing AI Output from the AI Services to create models that compete with Jobs'n'Profiles;",
                "divulging any information related to the AI Services to third parties, as such information is considered confidential to Jobs'n'Profiles;",
                "employing any automated or programmatic method to extract data or output from the AI Services, including scraping, web harvesting, or web data extraction;",
                "misrepresenting AI Output from the AI Services as human-generated when it is not;",
                "transmitting or processing any personal information of children under 13 or the applicable age of digital consent in connection with the AI Services; or",
                "using any AI Services in violation of applicable laws or third-party rights or for unethical purposes.",
              ].map((item) => (
                <ListItem key={item} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemText
                    primary={`• ${item}`}
                    primaryTypographyProps={{ style: { color: "#475569" } }}
                  />
                </ListItem>
              ))}
            </List>
            <Typography sx={{ ...bodySx, mt: 2 }}>
              You hereby agree to indemnify Jobs&apos;n&apos;Profiles for any
              third-party claim it receives in connection with your breach of
              this provision.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box component="section" sx={sectionSx}>
            <Typography variant="h5" sx={headingSx}>
              9. Additional Policies and Modifications
            </Typography>
            <Typography sx={bodySx}>
              These Terms shall be read together with Jobs&apos;n&apos;Profiles{" "}
              <Link href="/terms-and-conditions" underline="hover">
                Terms and Conditions
              </Link>
              ,{" "}
              <Link href="/privacy-policy" underline="hover">
                Privacy Policy
              </Link>
              , and such terms that may be updated from time to time, all of
              which can be found at{" "}
              <Link
                href="https://www.jobsnprofiles.com"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                www.jobsnprofiles.com
              </Link>
              . These terms shall form part of the Terms. Jobs&apos;n&apos;Profiles
              reserves the right to change, modify, add or remove any of the
              terms contained in these Terms, at any time, for any reason. We
              will provide notice of such amendments by posting the revised
              Terms, or by providing written notice in any other manner at
              Jobs&apos;n&apos;Profiles&apos; sole discretion, and such
              amendments shall be effective immediately upon posting and shall
              apply to your subsequent access to or use of the subject matter.
              You acknowledge that by continuing to access or use our products,
              services, AI Services and/or website after we have posted changes
              to these Terms, you are agreeing to such modified terms. If you do
              not agree to such revised Terms, you must stop accessing or using
              our products, services, AI Services and/or website immediately.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box component="section" sx={sectionSx}>
            <Typography variant="h5" sx={headingSx}>
              10. Governing Law and Jurisdiction
            </Typography>
            <Typography sx={bodySx}>
              These Terms shall be governed by and construed in accordance with
              the laws of the State of Utah, United States of America, without
              reference to any conflict of laws. Any legal suit, action or
              proceeding arising out of, or related to, these Terms shall be
              instituted exclusively in the state or federal courts located in
              Salt Lake County, Utah.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Typography
            sx={{ color: "#64748B", textAlign: "center", fontSize: "0.95rem" }}
          >
            Copyright Jobs&apos;n&apos;Profiles / Jobs n Profiles Inc.{" "}
            {new Date().getFullYear()}
          </Typography>
        </Box>
      </Container>
      <Homefooter onRequestDemo={handleDemoOpen} />
      <DemoRequestDialog open={demoDialogOpen} onClose={handleDemoClose} />
    </Box>
  );
};

export default AITermsOfUse;
